<template>
  <main class="instructions-page">
    <section class="hero-section">
      <div class="hero-content">
        <p class="eyebrow">Museu Catavento</p>
        <h1>Sistema de Agendamento</h1>
        <p class="hero-copy">
          Organize sua visita com segurança: confira as regras, entenda os tipos de roteiro e acesse a plataforma pelo
          link enviado ao seu e-mail. Este sistema atende exclusivamente ao Museu Catavento, localizado na cidade de
          São Paulo - SP.
        </p>

        <div class="hero-actions">
          <router-link class="btn btn-primary" to="/login">Iniciar login</router-link>
          <a class="btn btn-outline-primary" href="#instrucoes">Ver instruções</a>
        </div>
      </div>

      <aside class="access-card">
        <span>Acesso por link mágico</span>
        <strong>Use um e-mail válido</strong>
        <p>O acesso à agenda acontece pelo link recebido no e-mail informado no login.</p>
      </aside>
    </section>

    <section class="notice-grid" aria-label="Avisos importantes">
      <article class="notice-card important">
        <span>Atenção</span>
        <h2>Use apenas uma aba</h2>
        <p>
          Não atualize a página nem abra o sistema em mais de uma aba durante o agendamento. Isso pode encerrar sua
          sessão e exigir um novo login.
        </p>
      </article>

      <article class="notice-card important">
        <span>Local da visita</span>
        <h2>Museu Catavento - São Paulo</h2>
        <p>
          O agendamento é exclusivo para visitas ao Museu Catavento na cidade de São Paulo - SP, Capital.
        </p>
      </article>

      <article class="notice-card">
        <span>Prazos</span>
        <h2>Agendamento antecipado</h2>
        <p>
          A consulta de datas respeita a antecedência mínima definida pelo sistema. Administradores podem visualizar
          datas fora dessa regra.
        </p>
      </article>

      <article class="notice-card">
        <span>Finais de semana e feriados</span>
        <h2>Visitação livre</h2>
        <p>
          Aos finais de semana, feriados, emendas e férias, a visita é livre, sem monitoria de educadores.
        </p>
      </article>
    </section>

    <section class="content-section">
      <div class="section-heading">
        <p class="eyebrow">Tipos de visita</p>
        <h2>Entenda as modalidades</h2>
      </div>

      <div class="visit-types">
        <article v-for="tipo in tiposVisita" :key="tipo.titulo" class="visit-card">
          <span>{{ tipo.titulo }}</span>
          <p>{{ tipo.descricao }}</p>
        </article>
      </div>
    </section>

    <section id="instrucoes" class="content-section">
      <div class="section-heading">
        <p class="eyebrow">Passo a passo</p>
        <h2>Como concluir seu agendamento</h2>
      </div>

      <div class="steps-grid">
        <article v-for="(etapa, index) in etapas" :key="etapa.titulo" class="step-card">
          <span class="step-number">{{ String(index + 1).padStart(2, "0") }}</span>
          <h3>{{ etapa.titulo }}</h3>
          <p>{{ etapa.descricao }}</p>
          <router-link v-if="etapa.link" :to="etapa.link">{{ etapa.linkTexto }}</router-link>
        </article>
      </div>
    </section>

    <section class="final-cta">
      <div>
        <p class="eyebrow">Tudo pronto?</p>
        <h2>Faça login para acessar a agenda</h2>
        <p>Depois de autenticado, você poderá consultar roteiros disponíveis e acompanhar suas solicitações.</p>
      </div>
      <router-link class="btn btn-primary" to="/login">Entrar no sistema</router-link>
    </section>
  </main>
</template>

<script>
export default {
  setup() {
    const tiposVisita = [
      {
        titulo: "Mediado",
        descricao:
          "Com mediação de conteúdo dos educadores do Museu nas seções do roteiro agendado. A visita possui média de 2 horas.",
      },
      {
        titulo: "Livre",
        descricao:
          "Fluxo livre, sem a presença de educador ou orientador de público. O tempo médio de permanência é de 2 horas.",
      },
      {
        titulo: "Orientado",
        descricao:
          "Para grupos que desejam visitar sem mediação, mas com breve descritivo das seções. Duração média de 1h30.",
      },
    ];

    const etapas = [
      {
        titulo: "Solicite o acesso",
        descricao:
          "Informe um e-mail válido na tela de login. O link de acesso será enviado para esse endereço.",
        link: "/login",
        linkTexto: "Ir para login",
      },
      {
        titulo: "Confira seu e-mail",
        descricao:
          "Abra o link recebido. Se ele não aparecer na caixa de entrada, verifique spam, lixo eletrônico ou tente novamente após alguns instantes.",
      },
      {
        titulo: "Mantenha uma única aba",
        descricao:
          "Durante o agendamento, não atualize a página e não abra outra aba do sistema. Isso evita perda de sessão.",
      },
      {
        titulo: "Escolha data e roteiro",
        descricao:
          "Na agenda, navegue pela data desejada, confira horários disponíveis e selecione a opção Reservar.",
      },
      {
        titulo: "Preencha o formulário",
        descricao:
          "Revise os dados da instituição, contatos, quantidade de visitantes e informações de acessibilidade antes de enviar.",
      },
      {
        titulo: "Acompanhe o status",
        descricao:
          "Depois do envio, acesse seus agendamentos para acompanhar se a solicitação está pendente, confirmada ou cancelada.",
      },
    ];

    return {
      etapas,
      tiposVisita,
    };
  },
};
</script>

<style scoped>
.instructions-page {
  background: #f5f7fa;
  color: var(--catavento-text, #243447);
  min-height: 100vh;
  padding: clamp(1.25rem, 3vw, 2.5rem) 0 3rem;
  text-align: left;
}

.hero-section,
.content-section,
.notice-grid,
.final-cta {
  width: min(1180px, calc(100% - 2rem));
  margin: 0 auto;
}

.hero-section {
  align-items: stretch;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 1rem;
  margin-bottom: 1rem;
}

.hero-content,
.access-card,
.notice-card,
.visit-card,
.step-card,
.final-cta {
  background: var(--catavento-surface, #fff);
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: var(--catavento-radius, 8px);
  box-shadow: 0 8px 22px rgba(20, 30, 45, 0.08);
}

.hero-content {
  padding: clamp(1.5rem, 4vw, 2.5rem);
}

.eyebrow {
  color: var(--catavento-orange, #cd5f3f);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.hero-content h1 {
  color: var(--catavento-blue, #165a96);
  font-size: clamp(2rem, 4vw, 3.25rem);
  margin: 0;
}

.hero-copy {
  color: var(--catavento-muted, #667085);
  font-size: 1.08rem;
  max-width: 760px;
  margin: 1rem 0 0;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.access-card {
  border-left: 5px solid var(--catavento-blue, #165a96);
  padding: 1.25rem;
}

.access-card span,
.notice-card span,
.visit-card span {
  color: var(--catavento-muted, #667085);
  display: block;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.access-card strong {
  color: var(--catavento-text, #243447);
  display: block;
  font-size: 1.35rem;
  line-height: 1.2;
  margin-top: 0.45rem;
}

.access-card p,
.notice-card p,
.visit-card p,
.step-card p,
.final-cta p {
  color: var(--catavento-muted, #667085);
  margin-bottom: 0;
}

.notice-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.notice-card {
  border-left: 5px solid var(--catavento-blue, #165a96);
  padding: 1rem;
}

.notice-card.important {
  border-left-color: var(--catavento-orange, #cd5f3f);
}

.notice-card h2 {
  font-size: 1.1rem;
  margin: 0.35rem 0 0.5rem;
}

.content-section {
  margin-top: 2rem;
}

.section-heading {
  margin-bottom: 1rem;
}

.section-heading h2 {
  color: var(--catavento-text, #243447);
  margin: 0;
}

.visit-types {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.visit-card {
  padding: 1rem;
}

.visit-card span {
  color: var(--catavento-blue, #165a96);
  font-size: 0.95rem;
}

.visit-card p {
  margin-top: 0.55rem;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.step-card {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem;
}

.step-number {
  align-items: center;
  background: #eff6ff;
  border-radius: 999px;
  color: var(--catavento-blue, #165a96);
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 900;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.step-card h3 {
  color: var(--catavento-text, #243447);
  font-size: 1rem;
  margin: 0;
}

.step-card a {
  color: var(--catavento-blue, #165a96);
  font-weight: 800;
  margin-top: auto;
}

.final-cta {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.25rem;
}

.final-cta h2 {
  color: var(--catavento-blue, #165a96);
  margin: 0;
}

@media (max-width: 992px) {
  .hero-section,
  .notice-grid,
  .visit-types,
  .steps-grid {
    grid-template-columns: 1fr 1fr;
  }

  .hero-content {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767px) {
  .hero-section,
  .content-section,
  .notice-grid,
  .final-cta {
    width: min(100% - 1rem, 1180px);
  }

  .hero-section,
  .notice-grid,
  .visit-types,
  .steps-grid {
    grid-template-columns: 1fr;
  }

  .final-cta {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
